// Which neighbour a key press on a post page follows (phase 090,
// `doc/base-ui-seo-pwa-i18n.md` 2.6): `ArrowLeft` the previous — older — post,
// `ArrowRight` the next — newer — one, and nothing for every press that
// belongs to something else.
//
// A press is left alone when another handler already took it, while an input
// method composes, with any modifier (Alt+← is the browser's Back), when it
// is held down (a held arrow would flip through the whole feed), when it
// lands in or comes from a field — a text field, a select, an editable
// region, a player that seeks with the arrows, a form — and while the
// lightbox or a dialog is open: PhotoSwipe moves between its slides with the
// same keys, and its handler is added after this one, so its
// `preventDefault` comes too late to tell.
//
// Pure, so the rule has a vitest spec without a DOM: the controller reads the
// facts off the event and the document and follows the answer's link.
export type Neighbour = "previous" | "next"

export interface ElementFacts {
  readonly tagName: string
  // The `contenteditable` attribute as written, `null` when absent.
  readonly contentEditable: string | null
}

export interface KeyFacts {
  readonly key: string
  readonly defaultPrevented: boolean
  readonly isComposing: boolean
  readonly repeat: boolean
  readonly altKey: boolean
  readonly ctrlKey: boolean
  readonly metaKey: boolean
  readonly shiftKey: boolean
  // The event's target and then each of its ancestors, nearest first.
  readonly target: readonly ElementFacts[]
  // `document.activeElement` and then each of its ancestors, nearest first.
  readonly focus: readonly ElementFacts[]
  readonly lightboxOpen: boolean
  readonly dialogOpen: boolean
}

// PhotoSwipe appends its root on open and removes it once closed.
export const LIGHTBOX = ".pswp"

// A native dialog and the Elements that open over the page: `el-dialog`
// holds a `dialog[open]`, `el-menu` and `el-popover` mirror their popover's
// state into `open`.
export const OPEN_DIALOGS = "dialog[open], el-dialog[open], el-menu[open], el-popover[open]"

const KEYS = new Map<string, Neighbour>([
  ["ArrowLeft", "previous"],
  ["ArrowRight", "next"],
])

const FIELDS = new Set(["INPUT", "TEXTAREA", "SELECT", "VIDEO", "AUDIO", "FORM"])

export function neighbourFor(facts: KeyFacts): Neighbour | null {
  const neighbour = KEYS.get(facts.key)
  if (neighbour === undefined) return null
  if (facts.defaultPrevented || facts.isComposing || facts.repeat) return null
  if (facts.altKey || facts.ctrlKey || facts.metaKey || facts.shiftKey) return null
  if (facts.lightboxOpen || facts.dialogOpen) return null
  if (insideField(facts.target) || insideField(facts.focus)) return null

  return neighbour
}

// An element of a field kind anywhere on the path, or an editable region:
// the nearest `contenteditable` decides, and only `"false"` turns it off.
function insideField(path: readonly ElementFacts[]): boolean {
  let editable: boolean | null = null

  for (const element of path) {
    if (FIELDS.has(element.tagName.toUpperCase())) return true
    if (editable === null && element.contentEditable !== null) {
      editable = element.contentEditable.trim().toLowerCase() !== "false"
    }
  }

  return editable === true
}

interface Walkable {
  readonly tagName: string
  readonly parentElement: Walkable | null
  getAttribute(name: string): string | null
}

// The facts of an element and its ancestors, nearest first.
export function pathOf(element: Walkable | null): ElementFacts[] {
  const path: ElementFacts[] = []

  for (let node = element; node !== null; node = node.parentElement) {
    path.push({ tagName: node.tagName, contentEditable: node.getAttribute("contenteditable") })
  }

  return path
}
